import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { isVehiclePhoto, resolveDocumentUrl } from "../database/documentUrl";
import { VHU_EXPIRED_LOTS_PAGE, VHU_LOT_EDIT, VHU_LOT_FORM } from "../../routes/routeConstants";
import { deleteVhuLot, loadVhuLots } from "./vhuLotService";

const STATUS_OPTIONS = [
  { value: "BROUILLON", label: "Brouillon" },
  { value: "OUVERT", label: "Ouvert" },
  { value: "CLOTURE", label: "Cloture" },
  { value: "ANNULE", label: "Annule" },
];

const statusColor = {
  BROUILLON: "secondary",
  OUVERT: "success",
  CLOTURE: "dark",
  ANNULE: "danger",
};

const statusLabel = (status) => STATUS_OPTIONS.find((item) => item.value === status)?.label || status || "-";

const effectiveStatus = (lot) => (
  lot.status === "OUVERT" && lot.dateFin && new Date(lot.dateFin) <= new Date() ? "CLOTURE" : lot.status
);
const isExpiredLot = (lot) => {
  if (!lot.dateFin) return false;
  let end = new Date(lot.dateFin);
  if (Number.isNaN(end.getTime())) {
    const match = String(lot.dateFin).match(/^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2}))?/);
    if (!match) return false;
    end = new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]), Number(match[4] || 0), Number(match[5] || 0));
  }
  return end <= new Date();
};

const vehicleLabel = (vehicle) => [vehicle.marque, vehicle.modele, vehicle.immatriculation || vehicle.numChassis].filter(Boolean).join(" - ") || `Vehicule #${vehicle.id}`;
const vehiclePhotos = (vehicle) => ((vehicle?.photos || vehicle?.documents || []).filter(isVehiclePhoto));
const lotPhotos = (lot) => (lot.vehicules || []).flatMap(vehiclePhotos);
const moneyLabel = (value) => `${Number(value || 0).toLocaleString("fr-FR")} MAD`;
const dateLabel = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const hasTime = String(value).includes("T");
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...(hasTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(date);
};
const isMobileVisible = (lot) => {
  const now = new Date();
  const start = lot.dateDebut ? new Date(lot.dateDebut) : null;
  const end = lot.dateFin ? new Date(lot.dateFin) : null;
  return lot.isActif !== false && lot.status === "OUVERT" && (!start || start <= now) && (!end || end > now);
};

const emptyFilters = { search: "", status: "", dateFrom: "", dateTo: "" };
const escapeExcel = (value) => String(value ?? "")
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

function VhuLotsAdminPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(location.state?.successMessage || null);
  const [photoViewer, setPhotoViewer] = useState({ open: false, photos: [], index: 0, title: "" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [filters, setFilters] = useState(emptyFilters);
  const currentLots = lots.filter((lot) => !isExpiredLot(lot));
  const filteredLots = useMemo(() => currentLots.filter((lot) => {
    const search = filters.search.trim().toLowerCase();
    const searchable = [lot.reference, lot.titre, lot.lieuStockage, ...(lot.vehicules || []).map(vehicleLabel)]
      .filter(Boolean).join(" ").toLowerCase();
    const status = effectiveStatus(lot);
    const endDate = lot.dateFin ? new Date(lot.dateFin) : null;
    const from = filters.dateFrom ? new Date(`${filters.dateFrom}T00:00:00`) : null;
    const to = filters.dateTo ? new Date(`${filters.dateTo}T23:59:59`) : null;
    return (!search || searchable.includes(search))
      && (!filters.status || status === filters.status)
      && (!from || (endDate && endDate >= from))
      && (!to || (endDate && endDate <= to));
  }), [currentLots, filters]);

  useEffect(() => {
    loadPageData();
  }, []);

  const loadPageData = async () => {
    setLoading(true);
    try {
      const lotResponse = await loadVhuLots();
      setLots(lotResponse?.data?.body || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const openNewLotForm = () => {
    navigate(VHU_LOT_FORM);
  };

  const exportExcel = () => {
    const headers = ["Référence", "Titre", "Statut", "Début", "Fin", "Lieu", "Véhicules", "Prix départ", "Estimation", "Offres", "Meilleur acheteur"];
    const rows = filteredLots.map((lot) => [
      lot.reference, lot.titre, statusLabel(effectiveStatus(lot)), dateLabel(lot.dateDebut), dateLabel(lot.dateFin),
      lot.lieuStockage, (lot.vehicules || []).map(vehicleLabel).join(", "), lot.prixDepart, lot.estimationDepart,
      lot.bidsCount || 0, lot.bestBidderNom,
    ]);
    const table = `<table><thead><tr>${headers.map(value => `<th>${escapeExcel(value)}</th>`).join("")}</tr></thead><tbody>${rows.map(row => `<tr>${row.map(value => `<td>${escapeExcel(value)}</td>`).join("")}</tr>`).join("")}</tbody></table>`;
    const html = `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="UTF-8"></head><body>${table}</body></html>`;
    const url = URL.createObjectURL(new Blob(["\uFEFF", html], { type: "application/vnd.ms-excel;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `lots-vhu-${new Date().toISOString().slice(0, 10)}.xls`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const openPhotoViewer = (photos, title, index = 0) => {
    if (!photos.length) return;
    setPhotoViewer({ open: true, title, photos, index });
  };

  const editLot = (lot) => {
    navigate(VHU_LOT_EDIT.replace(":id", lot.id), { state: { lot } });
  };

  const confirmDeleteLot = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteVhuLot(deleteTarget.id);
      setDeleteTarget(null);
      setSuccessMessage("Lot supprimé avec succès.");
      await loadPageData();
    } finally {
      setDeleting(false);
    }
  };

  const renderVehicleThumb = (vehicle, size = 56) => {
    const photos = vehiclePhotos(vehicle);
    const photo = photos[0];
    if (!photo) {
      return (
        <span className="vhu-thumb vhu-thumb-empty" style={{ width: size, height: size }}>
          <i className="ri-car-line fs-4 text-muted" />
        </span>
      );
    }
    return (
      <button
        type="button"
        className="vhu-thumb-button"
        onClick={(event) => {
          event.stopPropagation();
          openPhotoViewer(photos, vehicleLabel(vehicle));
        }}
      >
        <img src={resolveDocumentUrl(photo)} alt={photo?.filenameUser || photo?.filename || vehicleLabel(vehicle)} className="vhu-thumb" style={{ width: size, height: size }} />
        {photos.length > 1 && (
          <span className="vhu-photo-count">{photos.length}</span>
        )}
      </button>
    );
  };

  return (
    <div className="page-content auction-page vhu-lots-page">
      <Container fluid>
        <Breadcrumbs
          title="Lots VHU"
          breadcrumbItems={[{ title: "Modules", link: "#" }, { title: "Lots VHU", link: "#" }]}
          actions={(
            <div className="d-flex gap-2">
              <Button color="light" onClick={() => navigate(VHU_EXPIRED_LOTS_PAGE)}><i className="ri-time-line me-1" />Expirées</Button>
              <Button color="primary" onClick={openNewLotForm}>
              <i className="ri-add-line me-1" />
              Nouveau lot
              </Button>
            </div>
          )}
        />

        <div className="d-flex justify-content-end mb-3 d-lg-none">
          <Button color="warning" outline onClick={() => navigate(VHU_EXPIRED_LOTS_PAGE)}>
            <i className="ri-time-line me-1" />Voir les enchères expirées
          </Button>
        </div>

        {successMessage && (
          <Alert color="success" className="shadow-sm border-0">
            <i className="ri-checkbox-circle-line me-1" />
            {successMessage}
          </Alert>
        )}

        <Card className="mb-4 border-0 shadow-sm">
          <CardBody>
            <Row className="g-3 align-items-end">
              <Col lg={4} md={6}>
                <Label>Recherche</Label>
                <Input value={filters.search} onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))} placeholder="Référence, titre, lieu ou véhicule" />
              </Col>
              <Col lg={2} md={6}>
                <Label>Statut</Label>
                <Input type="select" value={filters.status} onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}>
                  <option value="">Tous</option>
                  {STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </Input>
              </Col>
              <Col lg={2} md={6}>
                <Label>Fin à partir du</Label>
                <Input type="date" value={filters.dateFrom} onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))} />
              </Col>
              <Col lg={2} md={6}>
                <Label>Fin jusqu'au</Label>
                <Input type="date" value={filters.dateTo} onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))} />
              </Col>
              <Col lg={2} md={12}>
                <div className="d-flex gap-2 flex-wrap">
                  <Button color="light" onClick={() => setFilters(emptyFilters)} title="Réinitialiser"><i className="ri-refresh-line" /></Button>
                  <Button color="success" onClick={exportExcel} disabled={!filteredLots.length}><i className="ri-file-excel-2-line me-1" />Excel</Button>
                </div>
              </Col>
              <Col xs={12}><span className="text-muted small">{filteredLots.length} lot(s) trouvé(s)</span></Col>
            </Row>
          </CardBody>
        </Card>

        <Row className="g-4">
          <Col xl={12}>
            {loading ? (
              <div className="text-center py-5"><Spinner color="primary" /></div>
            ) : !filteredLots.length ? (
              <div className="vhu-empty-state">
                <i className="ri-inbox-line" />
                <strong>Aucun lot a afficher</strong>
                <span>{currentLots.length ? "Aucun lot ne correspond aux filtres." : "Créez un nouveau lot VHU pour commencer."}</span>
              </div>
            ) : (
              <div className="vhu-lot-grid">
                {filteredLots.map((lot) => {
                  const photos = lotPhotos(lot);
                  const displayedStatus = effectiveStatus(lot);
                  return (
                      <Card className="h-100 auction-card vhu-lot-card" key={lot.id}>
                        <CardBody>
                          <div className="vhu-card-photo-row">
                            {(lot.vehicules || []).slice(0, 4).map((vehicle) => <span key={vehicle.id}>{renderVehicleThumb(vehicle, 42)}</span>)}
                            {!photos.length && <span className="vhu-empty-photo"><i className="ri-image-line" /> Sans photo</span>}
                          </div>
                          <div className="d-flex justify-content-between gap-2 mb-2">
                            <div className="min-w-0">
                              <h6 className="mb-1 text-truncate" title={lot.titre}>{lot.titre}</h6>
                              <div className="text-muted small">{lot.reference || `Lot #${lot.id}`} - {lot.lieuStockage || "Lieu non renseigné"}</div>
                            </div>
                            <div className="d-grid gap-1 justify-items-end">
                              <Badge color={statusColor[displayedStatus] || "secondary"}>{statusLabel(displayedStatus)}</Badge>
                              {isMobileVisible(lot) && <Badge color="success" className="vhu-mobile-badge">Mobile</Badge>}
                            </div>
                          </div>
                          <Row className="g-2 mb-2 vhu-card-metrics">
                            <Col xs={6}><span className="text-muted small d-block">Véhicules</span><strong>{lot.vehicules?.length || 0}</strong></Col>
                            <Col xs={6}><span className="text-muted small d-block">Meilleure offre</span><strong>{moneyLabel(lot.currentBestBid)}</strong></Col>
                            <Col xs={6}><span className="text-muted small d-block">Fin</span><strong>{dateLabel(lot.dateFin)}</strong></Col>
                            <Col xs={6}><span className="text-muted small d-block">Offres</span><strong>{lot.bidsCount || 0}</strong></Col>
                          </Row>
                          <div className="small text-muted mb-2 text-truncate">
                            {lot.bestBidderNom ? `Meilleur acheteur: ${lot.bestBidderNom}` : "Aucune offre reçue pour le moment"}
                          </div>
                          {!!(lot.vehicules || []).length && (
                            <div className="vhu-vehicle-bid-summary mb-2">
                              {(lot.vehicules || []).slice(0, 3).map((vehicle) => (
                                <div key={vehicle.id} className="d-flex justify-content-between gap-2 small">
                                  <span className="text-truncate">{vehicleLabel(vehicle)}</span>
                                  <strong>{vehicle.bidsCount || 0} offre(s) - {moneyLabel(vehicle.currentBestBid || lot.prixDepart)}</strong>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="d-flex flex-wrap gap-2">
                            <Button size="sm" color="light" onClick={() => editLot(lot)}>Modifier</Button>
                            <Button size="sm" color="light" disabled={!photos.length} onClick={() => openPhotoViewer(photos, lot.titre || "Photos du lot")}>Voir photos</Button>
                            <Button size="sm" color="light" className="text-danger" onClick={() => setDeleteTarget(lot)}>Supprimer</Button>
                          </div>
                        </CardBody>
                      </Card>
                  );
                })}
              </div>
            )}
          </Col>
        </Row>
      </Container>

      <Modal isOpen={Boolean(deleteTarget)} toggle={() => !deleting && setDeleteTarget(null)} backdrop="static">
        <ModalHeader toggle={() => !deleting && setDeleteTarget(null)}>Confirmer la suppression</ModalHeader>
        <ModalBody>
          Voulez-vous vraiment supprimer le lot « {deleteTarget?.titre || deleteTarget?.reference || deleteTarget?.id} » ? Cette action est irréversible.
        </ModalBody>
        <ModalFooter>
          <Button color="light" disabled={deleting} onClick={() => setDeleteTarget(null)}>Annuler</Button>
          <Button color="danger" disabled={deleting} onClick={confirmDeleteLot}>
            {deleting && <Spinner size="sm" className="me-2" />}Supprimer
          </Button>
        </ModalFooter>
      </Modal>

      {photoViewer.open && photoViewer.photos.length > 0 && (
        <Lightbox
          mainSrc={resolveDocumentUrl(photoViewer.photos[photoViewer.index])}
          nextSrc={resolveDocumentUrl(photoViewer.photos[(photoViewer.index + 1) % photoViewer.photos.length])}
          prevSrc={resolveDocumentUrl(photoViewer.photos[(photoViewer.index + photoViewer.photos.length - 1) % photoViewer.photos.length])}
          imageTitle={photoViewer.title}
          imageCaption={photoViewer.photos[photoViewer.index].filenameUser || photoViewer.photos[photoViewer.index].filename}
          onCloseRequest={() => setPhotoViewer((prev) => ({ ...prev, open: false }))}
          onMovePrevRequest={() => setPhotoViewer((prev) => ({
            ...prev,
            index: (prev.index + prev.photos.length - 1) % prev.photos.length,
          }))}
          onMoveNextRequest={() => setPhotoViewer((prev) => ({
            ...prev,
            index: (prev.index + 1) % prev.photos.length,
          }))}
          enableZoom={true}
        />
      )}
    </div>
  );
}

export default VhuLotsAdminPage;
